import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Table } from 'antd';

import moment from 'moment-timezone';
import { debounce } from 'lodash';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { formatWholeNumber } from '../../../core/utils/campaigns';
import { formatCampaignName, hasSpecialCharacters } from '../../../core/utils/campaignNaming';
import { useTimezone } from '../../../core/hooks/useTimezone';

const { Search } = Input;

const getColumns = (timezone) => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
    render: (text, record) => {
      return !hasSpecialCharacters(text) ? formatCampaignName(record, timezone) : text;
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
  },
  {
    title: 'Flight Dates',
    dataIndex: 'flightDates',
    key: 'flightDates',
    render: (_, record) => {
      const start = record.startDate ? moment(record.startDate).tz(timezone) : null;
      const end = record.endDate ? moment(record.endDate).tz(timezone) : null;
      return `${start ? start.format('MM/DD/YYYY') : ''} - ${end ? end.format('MM/DD/YYYY') : ''}`;
    },
  },
  {
    title: 'Impressions',
    dataIndex: 'impressions',
    key: 'impressions',
    render: (_, record) => {
      const impressions =
        record &&
        record.aggregateData &&
        record.aggregateData.overallData &&
        record.aggregateData.overallData.aggImpressions;
      return formatWholeNumber(impressions || 0);
    },
  },
];

const CampaignRequestorCloneDialog = ({ visible, setVisible, orgId, onAddSelections }) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const {
    fetchCampaignOrdersConnection,
    campaignOrdersConnection,
    loadingCampaignOrdersConnection,
    pagination,
    setPagination,
  } = useCampaignRequestorContext();

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    if (selectedRowKeys.length === 0) {
      onAddSelections([]);
      setVisible(false);
      return;
    }
    const selectedAdGroup = campaignOrdersConnection.filter((adGroup) =>
      selectedRowKeys.includes(adGroup.id)
    );
    onAddSelections(selectedAdGroup);
    setVisible(false);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleFetchCampaignOrders = debounce(({ skip = 0, first = 10, searchName = '' } = {}) => {
    fetchCampaignOrdersConnection({
      variables: {
        where: {
          AND: [
            {
              OR: [
                {
                  orgs_some: {
                    OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }],
                  },
                },
              ],
            },
            { status_not: 'DRAFT' },
            { status_not: 'PENDING' },
            { name_contains: searchName },
          ],
        },
        orderBy: 'createdAt_DESC',
        skip,
        first,
      },
    });
  }, 500);

  useEffect(() => {
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    handleFetchCampaignOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: 20,
            }}
          >
            <span>MY CAMPAIGNS</span>
            <div>
              <Button type="default" onClick={() => handleAddSelections()}>
                Add Selection
              </Button>
            </div>
          </div>
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={1000}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Search
            placeholder="Search a campaign"
            style={{
              width: 200,
            }}
            onSearch={(searchName) => {
              handleFetchCampaignOrders({ searchName });
            }}
            allowClear
          />
        </div>

        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={getColumns(effectiveTimezone)}
          pagination={pagination}
          dataSource={campaignOrdersConnection}
          loading={loadingCampaignOrdersConnection}
          onChange={(pagination) => {
            setPagination(pagination);
            handleFetchCampaignOrders({
              skip: (pagination.current - 1) * pagination.pageSize,
              first: pagination.pageSize,
            });
          }}
        />
      </Modal>
    </>
  );
};

export default CampaignRequestorCloneDialog;
